import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Activity, Compass, Users, BookmarkCheck, Megaphone, BookOpen, Handshake,
  Calendar, Heart, FileText, MessageSquare, Settings, MapPin, Video, Home,
  CreditCard, Star, Trash2, Search, Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SavedClinicians() {
  const { getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clinicians, setClinicians] = useState([]);
  const [removing, setRemoving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/patient/saved`, getAuthHeader());
      setClinicians(data.clinicians || []);
    } catch (e) {
      toast.error('Could not load your saved clinicians');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const removeSaved = async (id) => {
    setRemoving(id);
    setClinicians((prev) => prev.filter((c) => c.id !== id)); // optimistic
    try {
      await axios.post(`${API}/api/patient/saved/${id}`, {}, getAuthHeader());
      toast.success('Removed from your shortlist');
    } catch (e) {
      toast.error('Could not remove — reloading');
      load();
    } finally {
      setRemoving(null);
    }
  };

  const navItems = [
    { icon: Activity, label: 'Dashboard', href: '/patient/dashboard' },
    { icon: Compass, label: 'Care Navigator', href: '/patient/navigator' },
    { icon: Users, label: 'Find Clinicians', href: '/patient/find-clinicians' },
    { icon: Heart, label: 'Saved Clinicians', active: true, highlight: true },
    { icon: BookmarkCheck, label: 'Saved Searches', href: '/patient/saved-searches' },
    { icon: Megaphone, label: 'Post a Need', href: '/patient/care-requests' },
    { icon: BookOpen, label: 'Browse Marketplace', href: '/marketplace' },
    { icon: Handshake, label: 'Offers & Bookings', href: '/patient/offers' },
    { icon: Calendar, label: 'Appointments', href: '/patient/dashboard#appointments' },
    { icon: FileText, label: 'Documents', href: '/patient/documents' },
    { icon: MessageSquare, label: 'Messages', href: '/patient/messages' },
    { icon: Settings, label: 'Settings', href: '#' },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title="Your shortlist"
      subtitle="The clinicians you saved while browsing. Compare, book, or keep exploring."
      testId="saved-clinicians-page"
    >
      {loading ? (
        <div className="flex items-center justify-center py-24 text-cn-text-muted" data-testid="saved-loading">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : clinicians.length === 0 ? (
        <div className="cn-card p-12 text-center bg-gradient-to-br from-white to-cn-bg/60" data-testid="saved-empty">
          <div className="w-20 h-20 rounded-3xl bg-cn-terracotta-500/10 text-cn-terracotta-500 mx-auto flex items-center justify-center mb-4">
            <Heart className="w-10 h-10" />
          </div>
          <h3 className="cn-display text-2xl text-cn-text dark:text-white">No saved clinicians yet</h3>
          <p className="text-sm text-cn-text-muted mt-2 max-w-md mx-auto">
            Tap the heart on any clinician in the marketplace or your AI matches to build a shortlist you can come back to.
          </p>
          <div className="mt-5 flex items-center justify-center gap-2">
            <Button onClick={() => navigate('/patient/find-clinicians')} className="cn-btn cn-btn-primary" data-testid="saved-find-btn">
              <Search className="w-4 h-4" /> Find clinicians
            </Button>
            <Button onClick={() => navigate('/marketplace')} className="cn-btn cn-btn-ghost" data-testid="saved-browse-btn">
              <BookOpen className="w-4 h-4" /> Browse marketplace
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-cn-text-muted mb-4" data-testid="saved-count">
            {clinicians.length} saved {clinicians.length === 1 ? 'clinician' : 'clinicians'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {clinicians.map((c) => {
              const initials = (c.name || 'C').split(' ').map((n) => n[0]).join('').slice(0, 2);
              return (
                <div
                  key={c.id}
                  className="cn-card cn-card-hover bg-white dark:bg-[#0F172A] border border-cn-border/70 overflow-hidden flex flex-col"
                  data-testid={`saved-clinician-${c.id}`}
                >
                  <div className="relative aspect-[4/3] bg-cn-bone-200 overflow-hidden">
                    {c.photo_url ? (
                      <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cn-sage-200 to-cn-sage-400 text-white text-5xl font-display font-bold">
                        {initials}
                      </div>
                    )}
                    {c.review_count > 0 && c.rating != null && (
                      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1">
                        <Star className="w-3 h-3 fill-cn-terracotta-400 text-cn-terracotta-400" />
                        {c.rating}<span className="text-cn-text-muted font-normal">({c.review_count})</span>
                      </div>
                    )}
                    <button
                      onClick={() => removeSaved(c.id)}
                      disabled={removing === c.id}
                      data-testid={`saved-remove-${c.id}`}
                      aria-label="Remove from shortlist"
                      title="Remove from shortlist"
                      className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 active:scale-95 transition-transform"
                    >
                      <Heart className="w-4 h-4 fill-cn-terracotta-500 text-cn-terracotta-500" />
                    </button>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Link to={`/clinicians/${c.id}`} className="font-display font-semibold text-lg leading-snug hover:text-cn-primary transition-colors" data-testid={`saved-name-${c.id}`}>
                          {c.name}
                        </Link>
                        <div className="text-sm text-cn-text-muted">{c.license_type} · {c.years_of_experience || 0} yrs</div>
                      </div>
                      {c.hourly_rate != null && (
                        <div className="text-right flex-shrink-0">
                          <div className="font-display font-bold text-lg">${c.hourly_rate}<span className="text-xs text-cn-text-muted font-normal">/hr</span></div>
                        </div>
                      )}
                    </div>
                    {c.specialties?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {c.specialties.slice(0, 3).map((s) => (
                          <span key={s} className="text-[11px] bg-cn-bone-200 text-cn-text px-2 py-0.5 rounded-full">{s}</span>
                        ))}
                      </div>
                    )}
                    {c.accepts_insurance && (
                      <div className="mt-2.5 inline-flex items-center gap-1 text-[11px] font-semibold text-cn-primary">
                        <CreditCard className="w-3 h-3" /> Accepts insurance{c.insurance_verified ? ' · verified' : ''}
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between text-xs text-cn-text-muted">
                      {c.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {c.city}, {c.state}</span>}
                      <div className="flex gap-1">
                        {c.available_for_telehealth && <Video className="w-3.5 h-3.5" />}
                        {c.available_for_in_person && <Home className="w-3.5 h-3.5" />}
                      </div>
                    </div>
                    <div className="mt-auto pt-4 flex gap-2">
                      <Button
                        className="flex-1 cn-btn cn-btn-primary !py-2 text-sm"
                        onClick={() => navigate(`/patient/book/${c.id}`)}
                        data-testid={`saved-book-${c.id}`}
                      >
                        <Calendar className="w-4 h-4" /> Book
                      </Button>
                      <Button
                        className="cn-btn cn-btn-ghost !py-2 text-sm"
                        onClick={() => navigate(`/clinicians/${c.id}`)}
                        data-testid={`saved-view-${c.id}`}
                      >
                        View profile
                      </Button>
                      <Button
                        className="cn-btn cn-btn-ghost !py-2 text-cn-text-muted hover:text-red-500"
                        onClick={() => removeSaved(c.id)}
                        disabled={removing === c.id}
                        aria-label="Remove"
                        data-testid={`saved-remove-btn-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </DashboardShell>
  );
}
